import Background from "@/assets/login-background.svg?react";
import { useIntl } from "@/infra/locale";
import { useAuth } from "@akina/infra";
import { Button, ExternalLink, Icon, Logo, Text } from "@akina/presentation";

type GuardType = "physiotherapistDisconnected" | "prescriptionExpired";

type InvalidPatientStatusGuardProps = Readonly<{
  icon: "search" | "hourglassRounded";
  titleId: `settings.account.${GuardType}.title`;
  descriptionId: `settings.account.${GuardType}.description`;
  contactSupportId: `settings.account.${GuardType}.contactSupport`;
}>;

function InvalidPatientStatusGuard({ icon, titleId, descriptionId, contactSupportId }: InvalidPatientStatusGuardProps) {
  const { formatMessage } = useIntl();
  const { logout } = useAuth();

  return (
    <div className="relative flex h-screen w-full flex-col items-center justify-center gap-10 overflow-hidden bg-offwhite p-10">
      <Background className="absolute inset-0 z-10 h-screen max-h-full object-fill" />
      <Logo className="z-20 h-8" />

      <div className="no-scrollbar relative z-20 flex max-w-[548px] flex-col gap-6 overflow-y-auto rounded-3xl bg-white p-10 shadow-2xl">
        <button className="absolute right-10 top-10 text-sm font-medium text-primary-100" onClick={() => logout()}>
          {formatMessage({
            id: "dashboard.navigationBar.profileMenu.logOut",
          })}
        </button>
        <Icon type={icon} className="mt-2 self-center text-gray-800/10" sizeClasses="h-45 w-45" />
        <div className="flex flex-col gap-8">
          <Text size="3xl" className="text-center font-bold">
            {formatMessage({
              id: titleId,
            })}
          </Text>
          <Text className="text-center">
            {formatMessage({
              id: descriptionId,
            })}
          </Text>
          <ExternalLink
            className="flex w-full justify-center"
            href={`mailto:${formatMessage({ id: "shared.supportEmail" })}`}>
            <Button className="mx-auto max-w-fit object-contain text-right">
              <Text className="text-center text-white">
                {formatMessage({
                  id: contactSupportId,
                })}
              </Text>
            </Button>
          </ExternalLink>
        </div>
      </div>
    </div>
  );
}

export function PhysiotherapistDisconnectedGuard() {
  const guardType = "physiotherapistDisconnected";
  return (
    <InvalidPatientStatusGuard
      icon="search"
      titleId={`settings.account.${guardType}.title`}
      descriptionId={`settings.account.${guardType}.description`}
      contactSupportId={`settings.account.${guardType}.contactSupport`}
    />
  );
}

export function PrescriptionExpiredGuard() {
  const guardType = "prescriptionExpired";
  return (
    <InvalidPatientStatusGuard
      icon="hourglassRounded"
      titleId={`settings.account.${guardType}.title`}
      descriptionId={`settings.account.${guardType}.description`}
      contactSupportId={`settings.account.${guardType}.contactSupport`}
    />
  );
}
